/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import React, { type ReactNode, type MouseEvent, forwardRef } from 'react';
import ArrowLeft from '@atlaskit/icon/core/arrow-left';
import ArrowRight from '@atlaskit/icon/core/arrow-right';
import { MediaFilmStripListItemSelector } from '.';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';

const filmStripViewStyles = css({
	position: 'relative',
	paddingTop: token('space.025'),
	paddingRight: 0,
	paddingBottom: token('space.025'),
	paddingLeft: 0,
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&:hover .arrow': {
		opacity: 1,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.ellipsed-text': {
		whiteSpace: 'initial',
	},
});

const filmStripListWrapperStyles = css({
	width: 'inherit',
	overflow: 'hidden',
	paddingTop: token('space.025'),
	paddingRight: token('space.025'),
	paddingBottom: token('space.025'),
	paddingLeft: token('space.025'),
});

const filmStripListStyles = css({
	margin: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- !important is used here to override padding coming from renderer RendererStyleContainer.tsx.
	padding: '0 !important',
	transitionProperty: 'transform',
	transitionTimingFunction: 'cubic-bezier(0.77, 0, 0.175, 1)',
	whiteSpace: 'nowrap',
	display: 'inline-block',
});

const filmStripListItemStyles = css({
	listStyleType: 'none',
	margin: 0,
	paddingTop: 0,
	paddingRight: token('space.050'),
	paddingBottom: 0,
	paddingLeft: token('space.050'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':first-of-type': {
		paddingLeft: 0,
	},
	display: 'inline-block',
	verticalAlign: 'middle',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: 0,
});

const arrowWrapperStyles = css({
	position: 'absolute',
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
	top: '50%',
	transform: 'translateY(-50%)',
	backgroundColor: token('elevation.surface.overlay'),
	borderRadius: token('radius.full'),
	display: 'flex',
	cursor: 'pointer',
	transition: 'opacity 0.3s',
	boxShadow: token('elevation.shadow.overlay'),
	color: token('color.icon'),
	width: '30px',
	height: '30px',
	justifyContent: 'center',
	opacity: 0,
	'&:hover': {
		color: token('color.text.subtle'),
		backgroundColor: token('elevation.surface.overlay.hovered'),
	},
	'&:active': {
		color: token('color.text.selected'),
		backgroundColor: token('color.background.selected'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	svg: {
		height: '30px',
		width: '20px',
	},
});

const arrowLeftWrapperStyles = css({
	left: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	svg: {
		paddingRight: token('space.025'),
	},
});

const arrowRightWrapperStyles = css({
	right: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	svg: {
		paddingLeft: token('space.025'),
	},
});

const shadowStyles = css({
	position: 'absolute',
	zIndex: 10,
	height: '100%',
	top: 0,
	width: '2px',
	backgroundColor: token('color.border'),
});

const shadowLeftStyles = css({
	left: 0,
});

const shadowRightStyles = css({
	right: 0,
});

const ShadowLeft = ({ children }: { children: ReactNode }) => (
	<div css={[shadowStyles, shadowLeftStyles]}>{children}</div>
);

type OnClick = {
	onClick: (event: MouseEvent<HTMLDivElement>) => void;
};

export const ArrowLeftWrapper = ({
	children,
	onClick,
}: {
	children: ReactNode;
} & OnClick) => (
	// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable, @atlaskit/ui-styling-standard/no-classname-prop, @atlassian/a11y/click-events-have-key-events, @atlassian/a11y/no-static-element-interactions -- Ignored via go/DSP-18766
	<div css={[arrowWrapperStyles, arrowLeftWrapperStyles]} className="arrow" onClick={onClick}>
		{children}
	</div>
);

export const ShadowRight = ({ children }: { children: ReactNode }) => (
	<div css={[shadowStyles, shadowRightStyles]}>{children}</div>
);

export const ArrowRightWrapper = ({
	children,
	onClick,
}: {
	children: ReactNode;
} & OnClick) => (
	// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable, @atlaskit/ui-styling-standard/no-classname-prop, @atlassian/a11y/click-events-have-key-events, @atlassian/a11y/no-static-element-interactions -- Ignored via go/DSP-18766
	<div css={[arrowWrapperStyles, arrowRightWrapperStyles]} className="arrow" onClick={onClick}>
		{children}
	</div>
);

export const LeftArrow: React.FC<OnClick> = ({ onClick }: OnClick) => (
	<ShadowLeft>
		<ArrowLeftWrapper onClick={onClick}>
			<ArrowLeft color="currentColor" spacing="spacious" label="left" />
		</ArrowLeftWrapper>
	</ShadowLeft>
);

export const RightArrow: React.FC<OnClick> = ({ onClick }: OnClick) => (
	<ShadowRight>
		<ArrowRightWrapper onClick={onClick}>
			<ArrowRight color="currentColor" spacing="spacious" label="right" />
		</ArrowRightWrapper>
	</ShadowRight>
);
export const FilmStripViewWrapper = ({
	children,
	'data-testid': dataTestId,
}: {
	children: ReactNode;
	'data-testid': string | undefined;
}) => (
	<div css={filmStripViewStyles} data-testid={dataTestId}>
		{children}
	</div>
);

export type FilmStripListWrapperProps = {
	children: ReactNode;
	onWheel: (event: React.WheelEvent<HTMLDivElement>) => void;
	onTouchStart: (event: React.TouchEvent<Element>) => void;
	onTouchMove: (event: React.TouchEvent<Element>) => void;
	onTouchEnd: (event: React.TouchEvent<Element>) => void;
	'data-testid': string | undefined;
};

export const FilmStripListWrapper = forwardRef<HTMLDivElement, FilmStripListWrapperProps>(
	(
		{ children, onWheel, onTouchStart, onTouchMove, onTouchEnd, 'data-testid': dataTestId },
		ref,
	) => (
		<div
			css={filmStripListWrapperStyles}
			ref={ref}
			onWheel={onWheel}
			onTouchStart={onTouchStart}
			onTouchMove={onTouchMove}
			onTouchEnd={onTouchEnd}
			data-testid={dataTestId}
		>
			{children}
		</div>
	),
);

export type FilmStripListProps = {
	children: ReactNode;
	style: {
		transform: string;
		transitionProperty: string;
		transitionDuration: string;
	};
};

export const FilmStripList = React.forwardRef<HTMLUListElement, FilmStripListProps>(
	({ children, style }, ref) => (
		<ul
			css={[filmStripListStyles]}
			ref={ref as React.RefObject<HTMLUListElement>}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			style={style}
		>
			{children}
		</ul>
	),
);

export const FilmStripListItem = ({
	children,
	index,
}: {
	children: ReactNode;
	index: React.Key;
}) => (
	<li
		css={filmStripListItemStyles}
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		className={MediaFilmStripListItemSelector}
		data-testid="media-filmstrip-list-item"
		key={index}
	>
		{children}
	</li>
);
